import { Component, Ref, Vue } from 'vue-property-decorator'
import ModalConfirmation from '@/components/modals/ModalConfirmation.vue'
import AddCustomFilterModal from '@/components/customFilter/AddCustomFilterModal.vue'
import RenameCustomFilterModal from '@/components/customFilter/RenameCustomFilterModal.vue'
import {
    CustomFilterRenamed,
    initialShipmentsSelectedOption,
    initialShipmentsSidebarState,
    shipmentsCompleteArray,
    ShipmentsCustomFilter,
    ShipmentsSidebarState
} from '@/modules/shipments-module/components/sidebar/shipments-sidebar.model'
import { findNodeByPropertyValue } from '@/components/sideBar/sideBarUtils'
import ExpandedSideBar from '@/components/sideBar/ExpandedSideBar.vue'
import CompactSideBar from '@/components/sideBar/CompactSideBar.vue'
import {
    UIComponentCustomFilterRequest,
    UIComponentCustomFilterResponse
} from '@/api/services/common/ui-component/custom-filter/ui-component-custom-filter.dto'
import { VtSidebarCustomConfig } from '@/api/resources/common/shared/models/common.models'
import { store } from '@/store/store'
import { ShipmentsStoreHelper } from '@/modules/shipments-module/store/shipments-store.helper'
import { ShipmentsStoreState } from '@/modules/shipments-module/store/shipments-store.model'
import { HelperUtils } from '@/shared/util/helper-utils'
import { StoreUtils } from '@/shared/util/store-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { UiComponentUtils } from '@/shared/util/ui-component-utils'
import { ShipmentsUtil } from '@/modules/shipments-module/shared/utils/shipments.util'

@Component({
    components: {
        ModalConfirmation,
        AddCustomFilterModal,
        RenameCustomFilterModal
    }
})
export default class ShipmentsSidebar extends Vue {
    @Ref() readonly confirmationModal!: any
    @Ref() readonly renameCustomFilterModal!: any
    @Ref() readonly addCustomFilterModal!: any

    state: ShipmentsSidebarState

    get disableAddFilter(): boolean {
        return this.$route.name !== 'shipmentsGrid'
    }

    get sidebarSelectedOption(): ShipmentsStoreState['selectedOption'] {
        return ShipmentsStoreHelper.sidebarSelectedOption
    }

    get summaries(): ShipmentsStoreState['summaries'] {
        return ShipmentsStoreHelper.summaries
    }

    constructor() {
        super()
        this.state = initialShipmentsSidebarState()
    }

    private _loadSidebar(sidebarName?: string): void {
        const sidebar = this.state.sidebars.find(s => s.name === sidebarName)
        if (sidebar) {
            this.state.sidebar = sidebar
        }
    }

    private async _loadSelectedOption(optionId?: string, defaultOption = false): Promise<void> {
        const options = [ ...shipmentsCompleteArray, ...this.state.customOptions ]
        const option = findNodeByPropertyValue('id', optionId, options)
        if (option) {
            if (defaultOption) {
                this.state.defaultOption = option
            }
            await this.changeSelectedOption(option)
        }
    }

    /// custom config

    private async _loadCustomConfig(): Promise<void> {
        const customConfig =
            await UiComponentUtils.getCustomConfig<VtSidebarCustomConfig>(
                this.state.componentCode,
                store.state.labId)
        if (customConfig) {
            this._loadSidebar(customConfig.config.sidebarName)
            await this._loadSelectedOption(customConfig.config.defaultOptionId, true)
        }
    }

    private async _saveCustomConfig(): Promise<void> {
        const config: VtSidebarCustomConfig = {
            sidebarName: this.state.sidebar.name,
            defaultOptionId: this.state.defaultOption?.id
        }
        await UiComponentUtils.saveCustomConfig<VtSidebarCustomConfig>(
            this.state.componentCode,
            { config },
            store.state.labId)
    }

    /// custom options (filters)

    private async _loadCustomOptions(): Promise<void> {
        const response =
            await UiComponentUtils.getCustomFilter<ShipmentsCustomFilter>(
                this.state.componentCode,
                store.state.labId)
        if (response) {
            this.state.customOptions = response
        }
    }

    private async _addCustomOption(customFilterRequest: UIComponentCustomFilterRequest<ShipmentsCustomFilter>): Promise<boolean> {
        if (UiComponentUtils.validateCustomFilter(customFilterRequest)) {
            try {
                StoreUtils.updateLoading(true)

                const response =
                    await UiComponentUtils.createCustomFilter(
                        this.state.componentCode,
                        customFilterRequest,
                        store.state.labId)
                if (response) {
                    await this._loadCustomOptions()
                    return true
                }
            } finally {
                StoreUtils.updateLoading(false)
            }
        }
        return false
    }

    private async _removeCustomOption(customFilterId: number): Promise<boolean> {
        try {
            StoreUtils.updateLoading(true)

            const response =
                await UiComponentUtils.removeCustomFilter(
                    this.state.componentCode,
                    customFilterId,
                    store.state.labId)
            if (response !== null) {
                await this._loadCustomOptions()

                if (customFilterId === this.sidebarSelectedOption?.id) {
                    await this.changeSelectedOption(initialShipmentsSelectedOption)
                }
                return true
            }
        } finally {
            StoreUtils.updateLoading(false)
        }
        return false
    }

    private async _updateCustomOption(customFilter: UIComponentCustomFilterResponse<ShipmentsCustomFilter>): Promise<boolean> {
        if (UiComponentUtils.validateCustomFilter(customFilter)) {
            try {
                StoreUtils.updateLoading(true)

                const response =
                    await UiComponentUtils.updateCustomFilter(
                        customFilter.componentCode,
                        customFilter,
                        customFilter.id,
                        customFilter.laboratoryId)
                if (response) {
                    await this._loadCustomOptions()
                    return true
                }
            } finally {
                StoreUtils.updateLoading(false)
            }
        }
        return false
    }

    /// HOOKS ///

    async created(): Promise<void> {
        await ShipmentsStoreHelper.setSidebarSelectedOption(initialShipmentsSelectedOption)
    }

    async mounted(): Promise<void> {
        try {
            StoreUtils.updateLoading(true)
            await Promise.allSettled([
                this._loadCustomOptions(),
                ShipmentsUtil.loadSummaries()
            ])
            await this._loadCustomConfig()
        } finally {
            StoreUtils.updateLoading(false)
        }

        this.state.summaryInterval = HelperUtils.setSummaryInterval(ShipmentsUtil.loadSummaries)
    }

    beforeDestroy(): void {
        if (this.state.summaryInterval !== null) {
            clearInterval(this.state.summaryInterval)
        }
    }

    /// EVENT HANDLERS ///

    async expand(): Promise<void> {
        this.state.sidebar = ExpandedSideBar
        await this._saveCustomConfig()

        this.$nextTick(() => this.$emit('changed-width'))
    }

    async contract(): Promise<void> {
        this.state.sidebar = CompactSideBar
        await this._saveCustomConfig()

        this.$nextTick(() => this.$emit('changed-width'))
    }

    async setDefaultOption(option: any): Promise<void> {
        this.state.defaultOption = option
        await this._saveCustomConfig()
    }

    async changeSelectedOption(selectedOption: any): Promise<void> {
        await ShipmentsStoreHelper.setSidebarSelectedOption(selectedOption)
    }

    onCustomOptionAdded(): void {
        this.addCustomFilterModal.show()
    }

    onCustomOptionRemoved(customFilter: UIComponentCustomFilterResponse<ShipmentsCustomFilter>): void {
        this.confirmationModal.ask(async () => {
            if (await this._removeCustomOption(customFilter.id)) {
                ToastUtils.showToastSuccess(this.$tc('global.filterDeleted'))
            }
        })
    }

    onCustomOptionRenamed(customFilter: UIComponentCustomFilterResponse<ShipmentsCustomFilter>): void {
        const filter: CustomFilterRenamed = {
            ...customFilter,
            label: customFilter.filterName
        }
        this.renameCustomFilterModal.show(filter) // TODO: component RenameCustomFilterModal needs a review
    }

    onCustomOptionUpdated(customFilter: UIComponentCustomFilterResponse<ShipmentsCustomFilter>): void {
        this.confirmationModal.ask(async () => {
            const filter = {
                ...customFilter,
                filterCriteria: {
                    ...customFilter.filterCriteria,
                    filterModel: ShipmentsStoreHelper.filterModel,
                    gridType: ShipmentsStoreHelper.gridType
                }
            }
            if (await this._updateCustomOption(filter)) {
                ToastUtils.showToastSuccess(this.$tc('global.filterUpdated'))
            }
        })
    }

    /// OPERATIONS ///

    async addCustomOption(option: { label: string }): Promise<void> { // TODO: component RenameCustomFilterModal needs a review
        const filter: UIComponentCustomFilterRequest<ShipmentsCustomFilter> = {
            filterName: option.label,
            filterCriteria: {
                filterModel: ShipmentsStoreHelper.filterModel,
                gridType: ShipmentsStoreHelper.gridType
            }
        }
        if (await this._addCustomOption(filter)) {
            ToastUtils.showToastSuccess(this.$tc('global.filterAdded'))
        }
    }

    async renameCustomOption(customFilter: CustomFilterRenamed): Promise<void> { // TODO: component RenameCustomFilterModal needs a review
        const filter: UIComponentCustomFilterResponse<ShipmentsCustomFilter> = {
            ...customFilter,
            filterName: customFilter.label
        }
        if (await this._updateCustomOption(filter)) {
            await this.updateSelectedOption()
            ToastUtils.showToastSuccess(this.$tc('global.renamedFilter'))
        }
    }

    async updateSelectedOption(): Promise<void> {
        const customOptionSelectedOption = this.state.customOptions.find(customOption => customOption.id === this.sidebarSelectedOption?.id)
        if (customOptionSelectedOption) {
            await this.changeSelectedOption(customOptionSelectedOption)
        }
    }
}
